import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import myAxios from '../AxiosUrl';
import styles from './AICalling.module.css';
import { TestCall } from './AgentSettings';
import { CallDetail } from './CallHistory';

/**
 * AI Calling → Marketing: campaigns that call the shop's OWN customers (people who ordered before)
 * with an offer. The AI offers the picked products (P1, P2…), takes the order on the call
 * (a real order, status Call Done), records "interested / not interested", and puts anyone who
 * says "don't call" on the do-not-call list. One marketing call per customer per 30 days.
 */

const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const taka = (n) => `৳${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const STATUS = {
    draft: ['Draft', styles.pillGrey],
    running: ['Running', styles.pillGreen],
    paused: ['Paused', styles.pillAmber],
    stopped: ['Stopped', styles.pillRed],
    completed: ['Completed', styles.pillBlue],
};
const RESULT = { ordered: ['Ordered', styles.pillGreen], interested: ['Interested', styles.pillBlue], not_interested: ['Not interested', styles.pillGrey], opted_out: ["Don't call", styles.pillRed] };
const PROMPT_EXAMPLE =
    'This week only (until Friday): Zafran Honey Soap 280 taka instead of 400. It is made with honey and saffron and suits dry skin.\nIf they buy 3, delivery inside Dhaka is free.\nIf they ask about other products, say our full list is on the website.';
const EMPTY = { name: '', prompt: '', products: [], audience: { months: 6, completedOnly: true, minOrders: 1, products: [] }, dailyLimit: 50, spendCapBdt: 1000, windowStart: '10:00', windowEnd: '20:00' };

function ProductPicker({ value, onChange, max, placeholder }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const res = await myAxios.get('call/campaign-products', { params: { q: q.trim() } });
                setResults(res.data?.products || []);
            } catch (err) {
                setResults([]);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [q]);
    const has = (id) => value.some((p) => String(p._id) === String(id));
    return (
        <div>
            {value.length > 0 && (
                <div className={styles.chips} style={{ marginTop: 0 }}>
                    {value.map((p, i) => (
                        <span key={p._id} className={`${styles.chip} ${styles.chipActive}`}>
                            {max === 10 ? `P${i + 1}. ` : ''}{p.name} · {taka(p.price)}
                            <button type="button" className={styles.chipX} onClick={() => onChange(value.filter((x) => x._id !== p._id))} aria-label={`Remove ${p.name}`}>×</button>
                        </span>
                    ))}
                </div>
            )}
            {value.length < max && (
                <>
                    <input className={styles.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} />
                    <div className={styles.pickList}>
                        {results.filter((p) => !has(p._id)).slice(0, 8).map((p) => (
                            <button type="button" key={p._id} className={styles.pickItem} onClick={() => onChange([...value, p])}>
                                <span>{p.name}</span>
                                <span className={styles.muted}>{taka(p.price)} · {p.stock > 0 ? `${p.stock} in stock` : 'out of stock'}</span>
                            </button>
                        ))}
                        {!results.length && <div className={styles.muted} style={{ padding: 8 }}>No products found.</div>}
                    </div>
                </>
            )}
        </div>
    );
}

function CampaignEditor({ initial, onClose, onSaved }) {
    const [f, setF] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [est, setEst] = useState(null);
    const set = (patch) => setF((x) => ({ ...x, ...patch }));
    const setA = (patch) => setF((x) => ({ ...x, audience: { ...x.audience, ...patch } }));
    const body = () => ({
        name: f.name,
        prompt: f.prompt,
        productIds: f.products.map((p) => p._id),
        audience: { months: Number(f.audience.months), completedOnly: f.audience.completedOnly, minOrders: Number(f.audience.minOrders), productIds: (f.audience.products || []).map((p) => p._id) },
        dailyLimit: Number(f.dailyLimit),
        spendCapBdt: Number(f.spendCapBdt),
        windowStart: f.windowStart,
        windowEnd: f.windowEnd,
    });
    const estimate = async () => {
        try {
            const res = await myAxios.post('call/campaigns/estimate', { ...body(), campaignId: f._id });
            setEst(res.data);
        } catch (err) {
            toast.error(errMsg(err, 'Could not check'));
        }
    };
    const save = async () => {
        try {
            setBusy(true);
            const res = f._id ? await myAxios.put(`call/campaigns/${f._id}`, body()) : await myAxios.post('call/campaigns', body());
            toast.success(res.data?.message || 'Saved');
            onSaved(res.data.campaign);
        } catch (err) {
            toast.error(errMsg(err, 'Could not save'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>{f._id ? 'Edit campaign' : 'New marketing campaign'}</h2>
                <button className={styles.btnSecondary} onClick={onClose}>Back</button>
            </div>
            <div className={styles.field}>
                <span className={styles.fieldLabel}>Campaign name (only you see it)</span>
                <input className={styles.input} maxLength={80} value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Eid soap offer" />
            </div>
            <div className={styles.field} style={{ marginTop: 12 }}>
                <span className={styles.fieldLabel}>The offer — what the AI should tell customers (price, discount, until when, why it's good)</span>
                <textarea className={styles.textarea} rows={6} maxLength={4000} value={f.prompt} onChange={(e) => set({ prompt: e.target.value })} placeholder={PROMPT_EXAMPLE} />
            </div>
            <div className={styles.field} style={{ marginTop: 12 }}>
                <span className={styles.fieldLabel}>Products the AI may sell on the call (up to 10) — it uses their price, colours/sizes and stock</span>
                <ProductPicker value={f.products} onChange={(products) => set({ products })} max={10} placeholder="Search your products…" />
            </div>

            <h3 className={styles.cardTitle} style={{ margin: '20px 0 8px', fontSize: 15 }}>Who to call — your own customers</h3>
            <div className={styles.formGrid}>
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>Ordered within the last</span>
                    <select className={styles.select} value={f.audience.months} onChange={(e) => setA({ months: e.target.value })}>
                        {[1, 3, 6, 12, 24].map((m) => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
                    </select>
                </div>
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>At least this many orders</span>
                    <input className={styles.input} type="number" min="1" max="100" value={f.audience.minOrders} onChange={(e) => setA({ minOrders: e.target.value })} />
                </div>
            </div>
            <label className={styles.switchRow} style={{ marginTop: 10, fontWeight: 400 }}>
                <input type="checkbox" checked={f.audience.completedOnly} onChange={(e) => setA({ completedOnly: e.target.checked })} />
                <span>Only customers whose order was shipped or paid (Courier Done / Payment Received)</span>
            </label>
            <div className={styles.field} style={{ marginTop: 12 }}>
                <span className={styles.fieldLabel}>Only customers who bought one of these (optional)</span>
                <ProductPicker value={f.audience.products || []} onChange={(products) => setA({ products })} max={20} placeholder="Any product — or search to narrow it…" />
            </div>

            <h3 className={styles.cardTitle} style={{ margin: '20px 0 8px', fontSize: 15 }}>Limits</h3>
            <div className={styles.formGrid}>
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>New customers per day</span>
                    <input className={styles.input} type="number" min="1" max="5000" value={f.dailyLimit} onChange={(e) => set({ dailyLimit: e.target.value })} />
                </div>
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>Spending cap (৳, 0 = no cap) — the campaign pauses at it</span>
                    <input className={styles.input} type="number" min="0" value={f.spendCapBdt} onChange={(e) => set({ spendCapBdt: e.target.value })} />
                </div>
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>Call hours</span>
                    <div className={styles.row} style={{ flexWrap: 'nowrap' }}>
                        <input className={styles.input} type="time" value={f.windowStart} onChange={(e) => set({ windowStart: e.target.value })} aria-label="From" />
                        <span className={styles.muted}>to</span>
                        <input className={styles.input} type="time" value={f.windowEnd} onChange={(e) => set({ windowEnd: e.target.value })} aria-label="To" />
                    </div>
                </div>
            </div>
            <p className={styles.hint}>
                Every customer gets at most one marketing call from your shop every 30 days, and anyone who says "don't call" is never called again. No calls 10 pm – 8 am.
            </p>
            {est && (
                <div className={styles.ok}>
                    <b>{est.reach.toLocaleString()}</b> customer{est.reach === 1 ? '' : 's'} can be called now
                    {est.excluded ? ` (${est.excluded} left out: do-not-call or called in the last 30 days)` : ''}. If all answer, about <b>{taka(est.estCostBdt)}</b> (≈ {taka(est.perCallBdt)} per answered call).
                </div>
            )}
            <div className={styles.row} style={{ justifyContent: 'flex-end', marginTop: 12 }}>
                <button className={styles.btnSecondary} onClick={estimate}>Check reach</button>
                <button className={styles.btnPrimary} onClick={save} disabled={busy}>{busy ? 'Saving…' : f._id ? 'Save' : 'Save as draft'}</button>
            </div>
        </div>
    );
}

function CampaignDetail({ id, hasNumber, balance, onBack, onEdit, onChanged }) {
    const [c, setC] = useState(null);
    const [calls, setCalls] = useState([]);
    const [open, setOpen] = useState(null);
    const load = useCallback(async () => {
        try {
            const [a, b] = await Promise.all([myAxios.get(`call/campaigns/${id}`), myAxios.get('call/calls', { params: { campaignId: id, limit: 50 } })]);
            setC(a.data.campaign);
            setCalls(b.data.calls || []);
        } catch (err) {
            toast.error(errMsg(err, 'Could not load the campaign'));
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);
    const act = async (action) => {
        if (action === 'stop' && !window.confirm('Stop this campaign for good? Customers waiting in its queue are not called.')) return;
        try {
            const res = await myAxios.post(`call/campaigns/${id}/action`, { action });
            toast.success(res.data?.message || 'Done');
            load();
            onChanged();
        } catch (err) {
            toast.error(errMsg(err, 'Could not update'));
        }
    };
    if (!c) return <div className={styles.card}><div className={styles.empty}>Loading…</div></div>;
    const s = c.stats || {};
    const st = STATUS[c.status] || [c.status, styles.pillGrey];
    return (
        <>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div>
                        <h2 className={styles.cardTitle}>{c.name} <span className={`${styles.pill} ${st[1]}`} style={{ marginLeft: 6 }}>{st[0]}</span></h2>
                        <div className={styles.muted} style={{ marginTop: 4 }}>
                            {c.launchedAt ? `Started ${fmtDate(c.launchedAt)}` : 'Not started yet'} · {c.dailyLimit} new customers a day · {c.windowStart}–{c.windowEnd}
                            {c.pausedReason === 'spend_cap' ? ' · paused: spending cap reached' : ''}
                        </div>
                    </div>
                    <div className={styles.row}>
                        <button className={styles.btnSecondary} onClick={onBack}>Back</button>
                        {['draft', 'paused', 'running'].includes(c.status) && <button className={styles.btnSecondary} onClick={() => onEdit(c)}>Edit</button>}
                        {['draft', 'paused'].includes(c.status) && <button className={styles.btnPrimary} onClick={() => act('start')}>{c.status === 'draft' ? 'Start' : 'Resume'}</button>}
                        {c.status === 'running' && <button className={styles.btnSecondary} onClick={() => act('pause')}>Pause</button>}
                        {!['stopped', 'completed', 'draft'].includes(c.status) && <button className={styles.btnDanger} onClick={() => act('stop')}>Stop</button>}
                    </div>
                </div>
                <div className={styles.grid4}>
                    {[
                        ['Customers', s.customers || 0],
                        ['Answered', s.answered || 0],
                        ['Ordered', s.ordered || 0],
                        ['Revenue', taka(s.revenueBdt)],
                        ['Interested', s.interested || 0],
                        ["Don't call", s.optedOut || 0],
                    ].map(([label, value]) => (
                        <div key={label} className={styles.card}>
                            <span className={styles.statLabel}>{label}</span>
                            <div className={styles.statValueSm}>{value}</div>
                        </div>
                    ))}
                </div>
                <p className={styles.hint} style={{ marginTop: 0 }}>
                    Spent {taka(s.costBdt)}{c.spendCapBdt > 0 ? ` of ${taka(c.spendCapBdt)}` : ''} · {c.waiting || 0} waiting to be called now.
                    {c.products?.length ? ` Offering: ${c.products.map((p, i) => `P${i + 1} ${p.name}`).join(', ')}.` : ''}
                </p>
            </div>
            {!['stopped', 'completed'].includes(c.status) && <TestCall kind="marketing" campaignId={c._id} hasNumber={hasNumber} balance={balance} label="Test this campaign on your phone" />}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Calls in this campaign</h2>
                    <button className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={load}>Refresh</button>
                </div>
                {calls.length === 0 ? (
                    <div className={styles.empty}>No calls yet.</div>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>Time</th><th>Customer</th><th>Result</th><th>Order</th><th>Length</th><th>Cost</th><th></th></tr></thead>
                            <tbody>
                                {calls.map((x) => {
                                    const r = RESULT[x.marketingResult];
                                    return (
                                        <tr key={x._id}>
                                            <td className={styles.num}>{fmtDateTime(x.at)}</td>
                                            <td className={styles.num}>{x.mobile}</td>
                                            <td>{r ? <span className={`${styles.pill} ${r[1]}`}>{r[0]}</span> : <span className={styles.muted}>{x.outcomeText}</span>}</td>
                                            <td className={styles.num}>{x.createdInvoice ? `#${x.createdInvoice}` : '—'}</td>
                                            <td className={styles.num}>{x.durationSec ? `${Math.floor(x.durationSec / 60)}:${String(x.durationSec % 60).padStart(2, '0')}` : '—'}</td>
                                            <td className={styles.num}>{taka(x.costBdt)}</td>
                                            <td><button className={styles.btnLink} onClick={() => setOpen(x._id)}>View</button></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {open && <CallDetail id={open} onClose={() => setOpen(null)} />}
        </>
    );
}

function MarketingCampaigns({ hasNumber, balance }) {
    const [list, setList] = useState(null);
    const [view, setView] = useState({ name: 'list' });
    const load = useCallback(async () => {
        try {
            const res = await myAxios.get('call/campaigns');
            setList(res.data.campaigns || []);
        } catch (err) {
            toast.error(errMsg(err, 'Could not load campaigns'));
            setList([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const edit = (c) =>
        setView({
            name: 'edit',
            initial: c
                ? { _id: c._id, name: c.name, prompt: c.prompt, products: c.products || [], audience: { ...c.audience, products: c.audience.products || [] }, dailyLimit: c.dailyLimit, spendCapBdt: c.spendCapBdt, windowStart: c.windowStart, windowEnd: c.windowEnd }
                : EMPTY,
        });

    if (view.name === 'edit') {
        return <CampaignEditor initial={view.initial} onClose={() => setView({ name: 'list' })} onSaved={(c) => { load(); setView({ name: 'detail', id: c._id }); }} />;
    }
    if (view.name === 'detail') {
        return <CampaignDetail id={view.id} hasNumber={hasNumber} balance={balance} onBack={() => setView({ name: 'list' })} onEdit={edit} onChanged={load} />;
    }
    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Marketing campaigns</h2>
                <button className={styles.btnPrimary} onClick={() => edit(null)}>New campaign</button>
            </div>
            <p className={styles.hint} style={{ marginTop: 0 }}>
                The AI calls people who ordered from your shop before, tells them your offer, answers their questions and takes the order on the call
                (it appears in your Order List as <b>Call Done</b>, source "AI Call"). One marketing call per customer every 30 days; "don't call" is respected forever.
            </p>
            {list === null ? (
                <div className={styles.empty}>Loading…</div>
            ) : list.length === 0 ? (
                <div className={styles.empty}>No campaigns yet. Create one, test it on your phone, then start it.</div>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead><tr><th>Campaign</th><th>Status</th><th>Customers</th><th>Answered</th><th>Ordered</th><th>Revenue</th><th>Spent</th><th></th></tr></thead>
                        <tbody>
                            {list.map((c) => {
                                const st = STATUS[c.status] || [c.status, styles.pillGrey];
                                return (
                                    <tr key={c._id}>
                                        <td><b>{c.name}</b><div className={styles.muted}>{c.products?.length ? c.products.map((p) => p.name).join(', ').slice(0, 60) : 'No products picked'}</div></td>
                                        <td><span className={`${styles.pill} ${st[1]}`}>{st[0]}</span></td>
                                        <td className={styles.num}>{c.stats?.customers || 0}</td>
                                        <td className={styles.num}>{c.stats?.answered || 0}</td>
                                        <td className={styles.num}>{c.stats?.ordered || 0}</td>
                                        <td className={styles.num}>{taka(c.stats?.revenueBdt)}</td>
                                        <td className={styles.num}>{taka(c.stats?.costBdt)}{c.spendCapBdt > 0 ? <div className={styles.muted}>of {taka(c.spendCapBdt)}</div> : null}</td>
                                        <td><button className={styles.btnLink} onClick={() => setView({ name: 'detail', id: c._id })}>Open</button></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default MarketingCampaigns;
